import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Users, Search, Send, Loader2, ChevronLeft, CheckCheck, Image, FileText, Video } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../services/api';

function Messages() {
    const [contacts, setContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const messagesEndRef = useRef(null);
    const messagesBodyRef = useRef(null);
    const lastMessageIdRef = useRef(null);   // track last seen message id
    const selectedContactRef = useRef(null); // always latest contact in polling
    const userScrolledUpRef = useRef(false); // track if user scrolled up

    useEffect(() => {
        loadContacts();
    }, []);

    // Keep ref in sync with state so polling closure always has latest contact
    useEffect(() => {
        selectedContactRef.current = selectedContact;
    }, [selectedContact]);

    // Smart polling: only fetch, don't auto-scroll unless user is at bottom
    useEffect(() => {
        if (!selectedContact) return;

        const interval = setInterval(() => {
            pollNewMessages();
        }, 5000);

        return () => clearInterval(interval);
    }, [selectedContact]);

    // Detect if user scrolled up (so we don't force-scroll them down)
    useEffect(() => {
        const container = messagesBodyRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            // If user is within 80px of bottom, consider them "at bottom"
            userScrolledUpRef.current = scrollHeight - scrollTop - clientHeight > 80;
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [selectedContact]);

    const scrollToBottom = (force = false) => {
        if (force || !userScrolledUpRef.current) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const loadContacts = async () => {
        try {
            const response = await API.get('/contacts');
            setContacts(response.data.data);
        } catch (error) {
            toast.error('Failed to load contacts');
        }
    };

    // Initial load — replace all messages, force scroll to bottom
    const loadMessages = async (contact) => {
        try {
            const response = await API.get(`/contacts/${contact.id}/messages`);
            const msgs = response.data.data;
            setMessages(msgs);
            if (msgs.length > 0) {
                lastMessageIdRef.current = msgs[msgs.length - 1].id;
            }
            // Force scroll on initial load
            setTimeout(() => scrollToBottom(true), 100);
        } catch (error) {
            // Silent fail
        }
    };

    // Smart poll — only append genuinely new messages
    const pollNewMessages = async () => {
        const contact = selectedContactRef.current;
        if (!contact) return;

        try {
            const response = await API.get(`/contacts/${contact.id}/messages`);
            const freshMsgs = response.data.data;
            if (!freshMsgs || freshMsgs.length === 0) return;

            const latestId = freshMsgs[freshMsgs.length - 1].id;

            // No new messages — do nothing at all
            if (latestId === lastMessageIdRef.current) return;

            // New messages arrived — update state
            lastMessageIdRef.current = latestId;
            setMessages(freshMsgs);

            // Only scroll if user is already near bottom
            scrollToBottom(false);
        } catch (error) {
            // Silent fail on polling
        }
    };

    const selectContact = async (contact) => {
        setSelectedContact(contact);
        selectedContactRef.current = contact;
        setShowChat(true);
        setLoading(true);
        userScrolledUpRef.current = false;
        lastMessageIdRef.current = null;
        try {
            await loadMessages(contact);
        } catch (error) {
            toast.error('Failed to load messages');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToContacts = () => {
        setShowChat(false);
        setSelectedContact(null);
        selectedContactRef.current = null;
        lastMessageIdRef.current = null;
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedContact) return;

        const toastId = toast.loading('Sending...');
        try {
            await API.post('/messages/send', {
                contactId: selectedContact.id,
                content: newMessage,
                messageType: 'text'
            });
            setNewMessage('');
            toast.success('Sent!', { id: toastId });
            // After sending, force scroll to bottom
            userScrolledUpRef.current = false;
            await loadMessages(selectedContact);
        } catch (error) {
            toast.error('Failed to send', { id: toastId });
        }
    };

    const getAvatarGradient = (index) => {
        const gradients = [
            'linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)',
            'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
            'linear-gradient(135deg, #0369a1 0%, #0284c7 100%)',
            'linear-gradient(135deg, #047857 0%, #059669 100%)',
            'linear-gradient(135deg, #b45309 0%, #d97706 100%)',
        ];
        return gradients[index % gradients.length];
    };

const getDateLabel = (dateString) => {
        const msgDate = new Date(dateString);
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        const isSameDay = (d1, d2) =>
            d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();

        if (isSameDay(msgDate, today)) return 'Today';
        if (isSameDay(msgDate, yesterday)) return 'Yesterday';

        return msgDate.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const renderMessageContent = (msg) => {
        const type = msg.message_type;
        if (type === 'image') {
            let mediaData = { url: '', caption: '' };
            try {
                mediaData = JSON.parse(msg.content);
            } catch {
                mediaData = { url: msg.content, caption: '' };
            }

            if (!mediaData.url) {
                return <div className="msg-bubble-text">🖼️ Image</div>;
            }

            return (
                <div className="msg-bubble-media">
                    <img
                        src={mediaData.url}
                        alt="media"
                        style={{
                            maxWidth: '220px',
                            width: '100%',
                            borderRadius: '8px',
                            display: 'block',
                            cursor: 'pointer'
                        }}
                        onClick={() => window.open(mediaData.url, '_blank')}
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentNode.querySelector('.img-fallback').style.display = 'flex';
                        }}
                    />
                    <div className="img-fallback" style={{
                        display: 'none',
                        alignItems: 'center',
                        gap: '6px',
                        opacity: 0.7,
                        fontSize: '13px'
                    }}>
                        <Image size={16} /> Image (tap to open)
                    </div>
                    {mediaData.caption && (
                        <div className="msg-bubble-text" style={{ marginTop: '4px' }}>
                            {mediaData.caption}
                        </div>
                    )}
                </div>
            );
        }

        if (type === 'video') {
            let mediaData = { url: '', caption: '' };
            try {
                mediaData = JSON.parse(msg.content);
            } catch {
                mediaData = { url: msg.content, caption: '' };
            }

            return (
                <div className="msg-bubble-media">
                    <video
                        src={mediaData.url}
                        controls
                        style={{ maxWidth: '220px', width: '100%', borderRadius: '8px' }}
                    />
                    {mediaData.caption && (
                        <div className="msg-bubble-text" style={{ marginTop: '4px' }}>
                            {mediaData.caption}
                        </div>
                    )}
                </div>
            );
        }

        if (type === 'document') {
            let docData = { url: '', filename: '', caption: '' };
            try {
                docData = JSON.parse(msg.content);
            } catch {
                docData = { url: '', filename: msg.content, caption: '' };
            }

            return (
                <div className="msg-bubble-text" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileText size={18} />
                    <span>
                        {docData.filename || 'Document'}
                        {docData.url && (
                            <a
                                href={docData.url}
                                target="_blank"
                                rel="noreferrer"
                                style={{ marginLeft: '6px', fontSize: '11px', opacity: 0.7 }}
                            >
                                Open
                            </a>
                        )}
                    </span>
                </div>
            );
        }

        if (type === 'interactive') {
            const parts = msg.content.split('\n[Buttons:');
            const bodyText = parts[0];
            const buttonText = parts[1] ? parts[1].replace(']', '') : '';

            return (
                <div>
                    <div className="msg-bubble-text">{bodyText}</div>
                    {buttonText && (
                        <div style={{
                            marginTop: '6px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '4px'
                        }}>
                            {buttonText.split('|').map((btn, i) => (
                                <span key={i} style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    border: '1px solid rgba(255,255,255,0.4)',
                                    borderRadius: '12px',
                                    padding: '2px 10px',
                                    fontSize: '12px'
                                }}>
                                    {btn.trim()}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return <div className="msg-bubble-text">{msg.content}</div>;
    };

    return (
        <div className="msg-wrapper">
            {/* ─── LEFT SIDEBAR ─── */}
            <div className={`msg-sidebar ${showChat ? 'msg-sidebar-hidden' : ''}`}>
                <div className="msg-sidebar-header">
                    <div className="msg-sidebar-header-icon">
                        <MessageSquare size={20} color="white" />
                    </div>
                    <div>
                        <div className="msg-sidebar-title">Conversations</div>
                        <div className="msg-sidebar-count">{contacts.length} contacts</div>
                    </div>
                </div>

                <div className="msg-search-wrap">
                    <Search size={14} className="msg-search-icon" />
                    <input
                        className="msg-search-input"
                        type="text"
                        placeholder="Search contacts..."
                        readOnly
                    />
                </div>

                <div className="msg-contacts-list">
                    {contacts.length === 0 ? (
                        <div className="msg-empty-contacts">
                            <div className="msg-empty-icon">
                                <Users size={48} color="#cbd5e1" />
                            </div>
                            <p>No contacts yet</p>
                        </div>
                    ) : (
                        contacts.map((contact, index) => (
                            <div
                                key={contact.id}
                                onClick={() => selectContact(contact)}
                                className={`msg-contact-item ${selectedContact?.id === contact.id ? 'msg-contact-active' : ''}`}
                            >
                                <div
                                    className="msg-contact-avatar"
                                    style={{ background: getAvatarGradient(index) }}
                                >
                                    {contact.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="msg-contact-info">
                                    <div className="msg-contact-name">{contact.name}</div>
                                    <div className="msg-contact-phone">{contact.phone}</div>
                                </div>
                                {selectedContact?.id === contact.id && (
                                    <div className="msg-contact-active-dot" />
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* ─── CHAT AREA ─── */}
            <div className={`msg-chat-area ${showChat || selectedContact ? 'msg-chat-visible' : ''}`}>
                {selectedContact ? (
                    <>
                        <div className="msg-chat-header">
                            <button onClick={handleBackToContacts} className="msg-back-btn">
                                <ChevronLeft size={16} /> Back
                            </button>
                            <div
                                className="msg-chat-avatar"
                                style={{ background: getAvatarGradient(contacts.findIndex(c => c.id === selectedContact.id)) }}
                            >
                                {selectedContact.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="msg-chat-header-info">
                                <div className="msg-chat-header-name">{selectedContact.name}</div>
                                <div className="msg-chat-header-phone">{selectedContact.phone}</div>
                            </div>
                            <div className="msg-chat-header-status">
                                <span className="msg-status-dot" />
                                <span className="msg-status-text">Active</span>
                            </div>
                        </div>

                        <div className="msg-messages-body" ref={messagesBodyRef}>
                            {loading ? (
                                <div className="msg-loading">
                                    <div className="msg-loading-spinner">
                                        <Loader2 size={36} style={{ animation: 'spin 1s linear infinite', color: '#1e3a5f' }} />
                                    </div>
                                    <p>Loading messages...</p>
                                </div>
                           ) : messages.length > 0 ? (
                                <>
                                    {(() => {
                                        // Messages ko date-wise groups mein todo
                                        const groups = [];
                                        messages.forEach((msg) => {
                                            const label = getDateLabel(msg.created_at);
                                            const lastGroup = groups[groups.length - 1];
                                            if (lastGroup && lastGroup.label === label) {
                                                lastGroup.msgs.push(msg);
                                            } else {
                                                groups.push({ label, msgs: [msg] });
                                            }
                                        });

                                        return groups.map((group) => (
                                            <div key={group.label + group.msgs[0].id}>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    margin: '14px 0',
                                                    position: 'sticky',
                                                    top: '8px',
                                                    zIndex: 5
                                                }}>
                                                    <span style={{
                                                        background: '#e2e8f0',
                                                        color: '#475569',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        padding: '4px 14px',
                                                        borderRadius: '14px',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                    }}>
                                                        {group.label}
                                                    </span>
                                                </div>
                                                {group.msgs.map((msg) => (
                                                    <div
                                                        key={msg.id}
                                                        className={`msg-bubble-wrap ${msg.direction === 'outgoing' ? 'msg-bubble-wrap-out' : 'msg-bubble-wrap-in'}`}
                                                    >
                                                        <div className={`msg-bubble ${msg.direction === 'outgoing' ? 'msg-bubble-out' : 'msg-bubble-in'}`}>
                                                            {renderMessageContent(msg)}
                                                            <div className="msg-bubble-meta">
                                                                {new Date(msg.created_at).toLocaleTimeString([], {
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}
                                                                {msg.direction === 'outgoing' && (
                                                                    <CheckCheck size={14} className="msg-tick" />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ));
                                    })()}
                                    <div ref={messagesEndRef} />
                                </>
                            ) : (
                                <div className="msg-no-messages">
                                    <div className="msg-no-messages-icon">
                                        <MessageSquare size={64} color="#cbd5e1" style={{ opacity: 0.5 }} />
                                    </div>
                                    <p>No messages yet. Start the conversation!</p>
                                </div>
                            )}
                        </div>

                        <form onSubmit={handleSend} className="msg-input-bar">
                            <input
                                type="text"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Type a message..."
                                className="msg-input-field"
                            />
                            <button
                                type="submit"
                                disabled={!newMessage.trim()}
                                className={`msg-send-btn ${newMessage.trim() ? 'msg-send-btn-active' : 'msg-send-btn-disabled'}`}
                            >
                                <span>Send</span>
                                <Send size={15} />
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="msg-empty-state">
                        <div className="msg-empty-state-icon">
                            <MessageSquare size={80} color="#cbd5e1" style={{ opacity: 0.3 }} />
                        </div>
                        <h3 className="msg-empty-state-title">Select a Conversation</h3>
                        <p className="msg-empty-state-text">Choose a contact from the sidebar to start chatting</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Messages;